import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, GraduationCap, ClipboardCheck, FileText, Bell, BookOpen, Clock, MessageSquare, AlertTriangle, Calendar } from "lucide-react";
import { getApiUrl } from "../../config/api";
import { normalizeRole } from "../../utils/authUtils";
import { useTenant } from "../../context/TenantContext";
import "../../styles/DashboardLayout.css";

interface DashboardData {
  schoolOverview: {
    id: number;
    name: string;
    status: string;
  };
  totals: {
    learners: number;
    staff: number;
    classes: number;
  };
  termStatus: {
    term: string;
    year: number;
    label: string;
    startDate: string;
    endDate: string;
  };
  resultsSummary?: {
    submittedThisTerm: number;
    pendingReview: number;
    classesMissingResults: number;
    missingResultsClasses?: Array<{ id: number; name: string }>;
  };
  reportCardsSummary?: {
    generatedThisTerm: number;
    pendingApproval: number;
  };
  attendanceSummary?: {
    present: number;
    absent: number;
    total: number;
    rate: number;
    classesNotMarked: number;
  };
  teacherWorkload?: {
    totalTeachers: number;
    teachersAssigned: number;
    teachersUnassigned: number;
    subjectsUnassigned: number;
    classesWithoutTeachers: number;
  };
  messaging?: {
    unread: number;
    recent: Array<{
      id: number;
      content: string;
      createdAt: string;
      senderName: string;
      subject: string;
    }>;
  };
  upcomingEvents?: Array<{
    id: string;
    name: string;
    date: string;
  }>;
  alerts?: {
    resultsMissing: number;
    attendanceNotMarked: number;
    classesWithoutTeachers: number;
    subjectsNotAssigned: number;
  };
  announcements?: Array<any>;
  recentActivity: {
    resultsSubmitted: Array<{
      id: number;
      examType: string;
      term?: string;
      year: number;
      updatedAt: string;
      studentName: string;
      subjectName: string;
      className: string;
    }>;
    reportsGenerated: Array<{
      reportName: string;
      createdAt: string;
      userEmail: string;
    }>;
    paymentsStatus: {
      pendingOrders: number;
      completedOrders: number;
    };
  };
}

const HeadteacherDashboard: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const { hasModule } = useTenant();
  const rawUserName = localStorage.getItem("userName") || "Headteacher";
  const userTitle = localStorage.getItem("userTitle") || "";
  const role = normalizeRole(localStorage.getItem("role"));
  const getDisplayName = () => {
    const safeName = rawUserName.trim();
    if (!safeName) return "Headteacher";
    const lastName = safeName.split(/\s+/).filter(Boolean).pop() || safeName;
    const honorific = userTitle ? (userTitle.endsWith(".") ? userTitle : `${userTitle}.`) : "Mr.";
    if (role === "headteacher" || role === "admin" || role === "teacher") {
      return `${honorific} ${lastName}`;
    }
    return safeName;
  };
  const userName = getDisplayName();

  const formatDate = (value?: string) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString();
  };
  const canUse = (moduleKey?: string) => !moduleKey || hasModule(moduleKey);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const response = await fetch(getApiUrl("headteacher/dashboard"), {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        });
        if (response.ok) {
          const payload = await response.json();
          setData(payload);
        }
      } catch (error) {
        console.error("Error fetching headteacher dashboard:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  return (
    <div className="dashboard-container">
      <main className="dashboard-main">
        <div className="page-title-section">
          <div>
            <h1 className="text-3xl font-black text-slate-900">Headteacher Dashboard</h1>
            <p className="page-subtitle text-base text-slate-600 mt-2">Welcome back, {userName}. Oversight and academic leadership.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <Users className="text-teal-600" />
              <div>
                <p className="text-base text-slate-500">Total Learners</p>
                <p className="text-2xl font-bold text-slate-900">{loading ? "..." : data?.totals.learners || 0}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <GraduationCap className="text-blue-600" />
              <div>
                <p className="text-base text-slate-500">Total Teachers</p>
                <p className="text-xl font-semibold text-slate-900">{loading ? "..." : data?.totals.staff || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <BookOpen className="text-indigo-600" />
              <div>
                <p className="text-base text-slate-500">Classes</p>
                <p className="text-2xl font-bold text-slate-900">{loading ? "..." : data?.totals.classes || 0}</p>
              </div>
            </div>
            <div className="text-base text-slate-600">
              {data?.schoolOverview?.name || "School"} Â· {data?.schoolOverview?.status || "active"}
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <Clock className="text-amber-600" />
              <div>
                <p className="text-base text-slate-500">Academic Term</p>
                <p className="text-2xl font-semibold text-slate-900">{data?.termStatus?.label || "Term Status"}</p>
              </div>
            </div>
            <p className="text-sm text-slate-500 mt-3">
              {data?.termStatus?.startDate} to {data?.termStatus?.endDate}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
          {canUse("results") && <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Results & Report Cards</h2>
              <FileText className="text-slate-400" />
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                <p className="text-sm text-slate-500">Results submitted (term)</p>
                <p className="text-2xl font-bold text-slate-900">{data?.resultsSummary?.submittedThisTerm || 0}</p>
              </div>
              <div className="p-4 rounded-xl bg-amber-50 border border-amber-100">
                <p className="text-sm text-amber-600">Results pending review</p>
                <p className="text-2xl font-bold text-amber-700">{data?.resultsSummary?.pendingReview || 0}</p>
              </div>
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                <p className="text-sm text-slate-500">Classes missing results</p>
                <p className="text-2xl font-bold text-slate-900">{data?.resultsSummary?.classesMissingResults || 0}</p>
              </div>
              <div className="p-4 rounded-xl bg-teal-50 border border-teal-100">
                <p className="text-sm text-teal-600">Report cards generated</p>
                <p className="text-2xl font-bold text-teal-700">{data?.reportCardsSummary?.generatedThisTerm || 0}</p>
              </div>
            </div>
            {(data?.resultsSummary?.missingResultsClasses || []).length > 0 && (
              <div className="text-sm text-slate-500 mb-4">
                Missing results: {(data?.resultsSummary?.missingResultsClasses || []).map((c) => c.name).join(", ")}
              </div>
            )}
            <div className="flex flex-wrap gap-3">
              <Link to="/headteacher/results" className="utopia-btn-secondary">View pending results</Link>
              <Link to="/report-cards/viewer" className="utopia-btn-secondary">Generate report cards</Link>
              <Link to="/headteacher/report-cards" className="utopia-btn-primary">View report cards</Link>
            </div>
          </div>}

          {canUse("attendance") && <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Attendance Summary</h2>
              <ClipboardCheck className="text-slate-400" />
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="p-4 rounded-xl bg-teal-50 border border-teal-100">
                <p className="text-sm text-teal-600">Attendance rate (today)</p>
                <p className="text-2xl font-bold text-teal-700">
                  {Math.round((data?.attendanceSummary?.rate || 0) * 100)}%
                </p>
              </div>
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                <p className="text-sm text-slate-500">Students present</p>
                <p className="text-2xl font-bold text-slate-900">{data?.attendanceSummary?.present || 0}</p>
              </div>
              <div className="p-4 rounded-xl bg-amber-50 border border-amber-100">
                <p className="text-sm text-amber-600">Students absent</p>
                <p className="text-2xl font-bold text-amber-700">{data?.attendanceSummary?.absent || 0}</p>
              </div>
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                <p className="text-sm text-slate-500">Classes not marked</p>
                <p className="text-2xl font-bold text-slate-900">{data?.attendanceSummary?.classesNotMarked || 0}</p>
              </div>
            </div>
            <Link to="/headteacher/attendance" className="utopia-btn-primary">Go to Attendance</Link>
          </div>}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
            <h2 className="text-xl font-bold mb-4">Teacher Workload</h2>
            <div className="space-y-3 text-base text-slate-600">
              <div className="flex justify-between">
                <span>Teachers assigned</span>
                <span className="font-semibold text-slate-900">{data?.teacherWorkload?.teachersAssigned || 0}</span>
              </div>
              <div className="flex justify-between">
                <span>Teachers unassigned</span>
                <span className="font-semibold text-slate-900">{data?.teacherWorkload?.teachersUnassigned || 0}</span>
              </div>
              <div className="flex justify-between">
                <span>Subjects not assigned</span>
                <span className="font-semibold text-slate-900">{data?.teacherWorkload?.subjectsUnassigned || 0}</span>
              </div>
              <div className="flex justify-between">
                <span>Classes without class teacher</span>
                <span className="font-semibold text-slate-900">{data?.teacherWorkload?.classesWithoutTeachers || 0}</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 mt-4">
              <Link to="/headteacher/class-teachers" className="utopia-btn-secondary">View class teachers</Link>
              <Link to="/headteacher/class-subjects" className="utopia-btn-secondary">Assign class subjects</Link>
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
            <h2 className="text-xl font-bold mb-4">System Alerts</h2>
            <div className="space-y-3">
              {[
                { label: "Results missing", value: data?.alerts?.resultsMissing || 0 },
                { label: "Attendance not marked", value: data?.alerts?.attendanceNotMarked || 0 },
                { label: "Classes without teacher", value: data?.alerts?.classesWithoutTeachers || 0 },
                { label: "Subjects not assigned", value: data?.alerts?.subjectsNotAssigned || 0 }
              ].map((item) => (
                <div key={item.label} className="flex items-center justify-between p-3 rounded-xl bg-amber-50 border border-amber-100">
                  <div className="flex items-center gap-2 text-base text-amber-700">
                    <AlertTriangle size={16} />
                    {item.label}
                  </div>
                  <span className="font-semibold text-amber-700 text-base">{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {canUse("notices") && <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Notices & Announcements</h2>
              <Bell className="text-slate-400" />
            </div>
            <div className="flex flex-wrap gap-3 mb-4">
              <Link to="/headteacher/notices" className="utopia-btn-primary">Create notice</Link>
            </div>
            {(data?.announcements || []).length === 0 ? (
              <p className="text-base text-slate-500">No announcements available. Create one to update staff and learners.</p>
            ) : (
              <ul className="space-y-3">
                {(data?.announcements || []).slice(0, 5).map((note, idx) => (
                  <li key={`${idx}-${typeof note === "string" ? note : "notice"}`} className="text-base text-slate-700 bg-slate-50 border border-slate-100 rounded-xl p-3">
                    {typeof note === "string" ? note : note?.title || note?.message || JSON.stringify(note)}
                  </li>
                ))}
              </ul>
            )}
          </div>}

          {canUse("messaging") && <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Messaging Summary</h2>
              <MessageSquare className="text-slate-400" />
            </div>
            <div className="flex items-center justify-between mb-4">
              <span className="text-base text-slate-600">Unread messages</span>
              <span className="text-xl font-bold text-slate-900">{data?.messaging?.unread || 0}</span>
            </div>
            {(data?.messaging?.recent || []).length === 0 ? (
              <p className="text-base text-slate-500">No recent messages.</p>
            ) : (
              <div className="space-y-3">
                {(data?.messaging?.recent || []).map((msg) => (
                  <div key={msg.id} className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                    <p className="text-sm text-slate-500">{msg.senderName} â€¢ {formatDate(msg.createdAt)}</p>
                    <p className="text-base font-semibold text-slate-800">{msg.subject || "Message"}</p>
                    <p className="text-base text-slate-600 line-clamp-2">{msg.content}</p>
                  </div>
                ))}
              </div>
            )}
            <Link to="/messages" className="utopia-btn-secondary mt-4 inline-flex">Open messages</Link>
          </div>}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Upcoming Events</h2>
              <Calendar className="text-slate-400" />
            </div>
            {(data?.upcomingEvents || []).length === 0 ? (
              <p className="text-base text-slate-500">No upcoming events. Add events in school settings.</p>
            ) : (
              <div className="space-y-3">
                {(data?.upcomingEvents || []).map((event) => (
                  <div key={event.id} className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                    <p className="text-base font-semibold text-slate-800">{event.name}</p>
                    <p className="text-sm text-slate-500">{formatDate(event.date)}</p>
                  </div>
                ))}
              </div>
            )}
            <Link to="/events" className="utopia-btn-secondary mt-4 inline-flex">View calendar</Link>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
          <h2 className="text-xl font-bold mb-4">Quick Links</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { path: "/headteacher/learners", label: "Learners", Icon: Users, module: "students" },
              { path: "/headteacher/teachers", label: "Teachers", Icon: GraduationCap, module: "teachers" },
              { path: "/headteacher/results", label: "Results Oversight", Icon: FileText, module: "results" },
              { path: "/headteacher/attendance", label: "Attendance", Icon: ClipboardCheck, module: "attendance" },
              { path: "/headteacher/notices", label: "Notices", Icon: Bell, module: "notices" },
              { path: "/headteacher/report-cards", label: "Report Cards", Icon: FileText, module: "report_cards" },
              { path: "/messages", label: "Messages", Icon: MessageSquare, module: "messaging" }
            ].filter(({ module }) => canUse(module)).map(({ path, label, Icon }) => (
              <Link key={path} to={path} className="p-4 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors text-center flex flex-col items-center gap-2">
                <Icon className="text-slate-600" />
                <span className="text-base font-semibold text-slate-700">{label}</span>
              </Link>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default HeadteacherDashboard;
